// Typed API client for the Eye Gallery backend (v4 + v5 contract).
// Wire shape mirrors `backend/src/validation/index.ts`. The backend
// returns raw JSON (no `{ ok, data }` envelope), so we parse it
// directly here. Errors come back as `{ error: { code, message } }`
// with non-2xx status; we normalise those to thrown `ApiError`.

import type { Parameters } from '../types/parameters';
import type { Annotation } from '../types/annotations';

const API_BASE: string =
  (import.meta.env.VITE_API_BASE as string | undefined) ?? '';

export class ApiError extends Error {
  public readonly code: string;
  public readonly status: number;
  constructor(code: string, message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
  }
}

/* ------------------------------------------------------------------ *
 * Wire types — mirror backend/src/validation/index.ts                 *
 * ------------------------------------------------------------------ */

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface ChatTurn {
  role: 'user' | 'assistant';
  content: string;
  /** v5 — assistant turns may carry a parameter diff for the diff bubble. */
  diff?: { parameter: string; before: unknown; after: unknown }[];
}

export interface ChatRequestBody {
  current_parameters: Parameters | null;
  current_image_url: string | null;
  current_seed: number | null;
  chat_history: ChatMessage[];
  user_message: string;
  n_variations?: 1 | 4;
}

export interface ChatResponseItem {
  id: string;
  seed: number;
  image_url: string;
  thumbnail_url: string;
  parameters: Parameters;
  explanation: string;
  parent_id: string | null;
  created_at: string;
  /** v5 — when the backend emits this, we surface it on the assistant turn. */
  diff?: { parameter: string; before: unknown; after: unknown }[];
}

export type ChatResponse = ChatResponseItem;
export type ChatVariationResponse = { items: ChatResponseItem[]; parent_id: string | null };

export function isVariationResponse(
  r: ChatResponse | ChatVariationResponse,
): r is ChatVariationResponse {
  return (
    typeof r === 'object' &&
    r !== null &&
    Array.isArray((r as ChatVariationResponse).items)
  );
}

export interface GenerateRequestBody {
  prompt?: string;
  parameters: Parameters;
  n_variations?: 1 | 4;
}

export interface GenerateResponse extends ChatResponseItem {}

export interface CatalogueItem {
  id: string;
  generated_id: string;
  title: string;
  notes: string | null;
  tags: string[];
  image_url: string;
  thumbnail_url: string;
  created_at: string;
  /** v5 — optional mood / colour-family tags (Hub may populate). */
  mood?: string | null;
  colour_family?: string | null;
  project_tag?: string | null;
  /** v5 — optional board id. */
  board_id?: string | null;
  /** v5 — back-reference parameters so the catalogue can be re-rendered
   *  without hitting the `/load` endpoint. */
  parameters?: Parameters | null;
  seed?: number | null;
  parent_id?: string | null;
  /** v7 — annotation hotspots (image-relative coords). */
  annotations?: Annotation[];
}

export interface CatalogueLoadResponse {
  id: string;
  generated_id: string;
  title: string;
  notes: string | null;
  parameters: Parameters;
  seed: number;
  image_url: string;
  thumbnail_url: string;
  parent_id: string | null;
  /** v5 — Hub may populate; the LineageStrip falls back to walking parent
   *  ids via repeated `/load` calls when this is absent. */
  lineage?: CatalogueLineageEntry[];
}

export interface CatalogueLineageEntry {
  id: string;
  image_url: string;
  thumbnail_url: string;
  seed: number;
  parameters: Parameters;
}

export interface Board {
  id: string;
  name: string;
  created_at: string;
}

export interface SaveCatalogueRequest {
  generated_id: string;
  title?: string;
  notes?: string | null;
  tags?: string[];
  /** v5 — optional metadata. Backend ignores fields it doesn't recognise. */
  mood?: string | null;
  colour_family?: string | null;
  project_tag?: string | null;
  board_id?: string | null;
  /**
   * v7 — annotation hotspots to persist alongside the catalogue row.
   * Image-relative coordinates (0..1).
   */
  annotations?: Annotation[];
}

export interface LooksheetResponse {
  canonical_image_id: string;
  items: LooksheetItem[];
}

export interface LooksheetItem {
  image_id: string;
  image_url: string;
  thumbnail_url: string;
  emotion: string;
  seed: number;
}

export interface UploadResponse {
  image_id: string;
  image_url: string;
  thumbnail_url: string;
  title?: string;
  notes?: string | null;
  created_at?: string;
}

export interface GalleryItem {
  id: string;
  url: string;
  thumbnail_url: string;
  title: string;
  notes: string | null;
  created_at: string;
}

/* ------------------------------------------------------------------ *
 * Request helpers                                                     *
 * ------------------------------------------------------------------ */

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      headers: {
        'Content-Type': 'application/json',
        ...(init?.headers ?? {}),
      },
      ...init,
    });
  } catch (e) {
    throw new ApiError(
      'network',
      e instanceof Error ? e.message : 'Network error',
      0,
    );
  }

  if (!res.ok) {
    let code = 'http_' + String(res.status);
    let message = `Request failed: ${res.status}`;
    try {
      const body = (await res.json()) as
        | { error?: { code: string; message: string } }
        | undefined;
      if (body?.error) {
        code = body.error.code;
        message = body.error.message;
      }
    } catch {
      /* ignore parse error */
    }
    throw new ApiError(code, message, res.status);
  }

  // 204 / empty body
  if (res.status === 204) return undefined as unknown as T;
  const contentType = res.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) {
    return undefined as unknown as T;
  }
  return (await res.json()) as T;
}

/**
 * Resolve a relative API asset URL (`/api/images/...`) against the
 * configured API base. Returns `undefined` when the input is empty /
 * null — this matches what `<img src>` expects and removes the need for
 * nullish-coalescing at every call site.
 */
export function resolveAssetUrl(
  url: string | null | undefined,
): string | undefined {
  if (!url) return undefined;
  if (/^https?:\/\//i.test(url)) return url;
  if (!API_BASE) return url;
  if (url.startsWith('/') && API_BASE.endsWith('/')) {
    return `${API_BASE}${url.slice(1)}`;
  }
  if (!url.startsWith('/') && !API_BASE.endsWith('/')) {
    return `${API_BASE}/${url}`;
  }
  return `${API_BASE}${url}`;
}

export const api = {
  base: API_BASE,

  // ─── Chat ───────────────────────────────────────────────────────────
  /**
   * POST /api/chat. The backend accepts v4 parameters with safe defaults
   * for v5 fields, so legacy v4 clients keep working.
   */
  async chat(
    req: ChatRequestBody,
    signal?: AbortSignal,
  ): Promise<ChatResponse | ChatVariationResponse> {
    return request<ChatResponse | ChatVariationResponse>('/api/chat', {
      method: 'POST',
      body: JSON.stringify(req),
      signal,
    });
  },

  // ─── Direct generate ────────────────────────────────────────────────
  /**
   * POST /api/generate. Fresh generation — new random seed, no
   * subject_reference (caller passes `parameters`).
   */
  async generateFresh(
    req: GenerateRequestBody,
    signal?: AbortSignal,
  ): Promise<ChatResponse | ChatVariationResponse> {
    return request<ChatResponse | ChatVariationResponse>('/api/generate', {
      method: 'POST',
      body: JSON.stringify(req),
      signal,
    });
  },

  // ─── Catalogue ──────────────────────────────────────────────────────
  async listCatalogue(signal?: AbortSignal): Promise<CatalogueItem[]> {
    return request<CatalogueItem[]>('/api/catalogue', { signal });
  },

  async saveToCatalogue(req: SaveCatalogueRequest): Promise<CatalogueItem> {
    return request<CatalogueItem>('/api/catalogue', {
      method: 'POST',
      body: JSON.stringify(req),
    });
  },

  async deleteCatalogueItem(id: string): Promise<void> {
    return request<void>(`/api/catalogue/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  },

  /** Back-compat alias. */
  async deleteCatalogue(id: string): Promise<void> {
    return this.deleteCatalogueItem(id);
  },

  /** v5 — load a saved eye as a starting point (full Parameters + seed). */
  async loadCatalogueItem(
    catalogueId: string,
    signal?: AbortSignal,
  ): Promise<CatalogueLoadResponse> {
    return request<CatalogueLoadResponse>(
      `/api/catalogue/${encodeURIComponent(catalogueId)}/load`,
      { signal },
    );
  },

  /** v5 — fetch the 5-colour palette for a saved eye. */
  async getPalette(
    catalogueId: string,
    signal?: AbortSignal,
  ): Promise<string[]> {
    return request<string[]>(
      `/api/catalogue/${encodeURIComponent(catalogueId)}/palette`,
      { signal },
    );
  },

  // ─── Boards (v5 — Hub endpoint; UI gracefully degrades on 404) ─────
  async listBoards(signal?: AbortSignal): Promise<Board[]> {
    return request<Board[]>('/api/boards', { signal });
  },

  async createBoard(name: string): Promise<Board> {
    return request<Board>('/api/boards', {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
  },

  async moveToBoard(
    catalogueId: string,
    boardId: string | null,
  ): Promise<void> {
    return request<void>(
      `/api/catalogue/${encodeURIComponent(catalogueId)}/move-to-board`,
      {
        method: 'POST',
        body: JSON.stringify({ board_id: boardId }),
      },
    );
  },

  // ─── Looksheet (v5) ─────────────────────────────────────────────────
  async generateLooksheet(
    canonicalImageUrl: string,
    canonicalSeed: number,
    emotions: string[],
    options?: { title?: string; notes?: string },
  ): Promise<LooksheetResponse> {
    return request<LooksheetResponse>('/api/looksheet', {
      method: 'POST',
      body: JSON.stringify({
        canonical_image_url: canonicalImageUrl,
        canonical_seed: canonicalSeed,
        emotions,
        title: options?.title,
        notes: options?.notes,
      }),
    });
  },

  // ─── Image download (v5) ────────────────────────────────────────────
  /**
   * Streams the PNG as a Blob. Throws ApiError on non-2xx.
   */
  async downloadImage(id: string, signal?: AbortSignal): Promise<Blob> {
    let res: Response;
    try {
      res = await fetch(
        `${API_BASE}/api/images/${encodeURIComponent(id)}/download`,
        signal ? { signal } : undefined,
      );
    } catch (e) {
      throw new ApiError(
        'network',
        e instanceof Error ? e.message : 'Network error',
        0,
      );
    }
    if (!res.ok) {
      const ct = res.headers.get('content-type') ?? '';
      let code = 'http_' + String(res.status);
      let message = `Download failed: ${res.status}`;
      if (ct.includes('application/json')) {
        try {
          const body = (await res.json()) as
            | { error?: { code: string; message: string } }
            | undefined;
          if (body?.error) {
            code = body.error.code;
            message = body.error.message;
          }
        } catch {
          /* ignore */
        }
      }
      throw new ApiError(code, message, res.status);
    }
    return res.blob();
  },

  // ─── Gallery ────────────────────────────────────────────────────────
  async listGallery(signal?: AbortSignal): Promise<GalleryItem[]> {
    return request<GalleryItem[]>('/api/gallery', { signal });
  },

  async deleteGallery(id: string): Promise<void> {
    return request<void>(`/api/gallery/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  },

  // ─── v7 — Plain-language description ────────────────────────────────
  /**
   * Fetch a deterministic plain-language description for a Parameters
   * object. Same parameters always return the same description — useful for
   * the catalogue so two rows with identical settings get identical copy.
   */
  async describe(parameters: Parameters, signal?: AbortSignal): Promise<string> {
    const res = await request<{ description: string }>('/api/description', {
      method: 'POST',
      body: JSON.stringify(parameters),
      signal,
    });
    return res.description;
  },

  // ─── v7 — Reference image upload (chat with reference) ───────────────
  /**
   * Upload a reference image that the next `/api/chat-with-reference` call
   * will pass as `input_urls` to MiniMax. Returns the URL the backend will
   * serve the image at.
   */
  async uploadReference(file: File): Promise<UploadResponse> {
    return new Promise<UploadResponse>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API_BASE}/api/chat-with-reference/upload`);
      xhr.upload.addEventListener('progress', () => {
        /* no-op for now; could surface a progress bar later */
      });
      xhr.addEventListener('error', () => {
        reject(
          new ApiError(
            'network',
            'Reference upload failed — check your connection',
            0,
          ),
        );
      });
      xhr.addEventListener('load', () => {
        if (xhr.status < 200 || xhr.status >= 300) {
          let code = 'http_' + String(xhr.status);
          let message = `Reference upload failed: ${xhr.status}`;
          try {
            const body = JSON.parse(xhr.responseText) as
              | { error?: { code: string; message: string } }
              | undefined;
            if (body?.error) {
              code = body.error.code;
              message = body.error.message;
            }
          } catch {
            /* ignore */
          }
          reject(new ApiError(code, message, xhr.status));
          return;
        }
        try {
          resolve(JSON.parse(xhr.responseText) as UploadResponse);
        } catch (e) {
          reject(
            new ApiError(
              'malformed_response',
              e instanceof Error ? e.message : 'Bad reference upload envelope',
              xhr.status,
            ),
          );
        }
      });
      const fd = new FormData();
      fd.append('file', file);
      xhr.send(fd);
    });
  },

  /**
   * Send a chat request that includes a reference image URL. The backend
   * forwards the URL as `input_urls` to MiniMax so the new generation is
   * guided by the reference.
   */
  async chatWithReference(
    req: ChatRequestBody & { reference_url: string },
    signal?: AbortSignal,
  ): Promise<ChatResponse | ChatVariationResponse> {
    return request<ChatResponse | ChatVariationResponse>(
      '/api/chat-with-reference',
      {
        method: 'POST',
        body: JSON.stringify(req),
        signal,
      },
    );
  },

  // ─── Uploads ────────────────────────────────────────────────────────
  async upload(
    file: File,
    onProgress?: (pct: number) => void,
  ): Promise<UploadResponse> {
    return new Promise<UploadResponse>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API_BASE}/api/uploads`);
      xhr.upload.addEventListener('progress', (ev) => {
        if (ev.lengthComputable && onProgress) {
          onProgress((ev.loaded / ev.total) * 100);
        }
      });
      xhr.addEventListener('error', () => {
        reject(
          new ApiError(
            'network',
            'Upload failed — check your connection',
            0,
          ),
        );
      });
      xhr.addEventListener('load', () => {
        if (xhr.status < 200 || xhr.status >= 300) {
          let code = 'http_' + String(xhr.status);
          let message = `Upload failed: ${xhr.status}`;
          try {
            const body = JSON.parse(xhr.responseText) as
              | { error?: { code: string; message: string } }
              | undefined;
            if (body?.error) {
              code = body.error.code;
              message = body.error.message;
            }
          } catch {
            /* ignore */
          }
          reject(new ApiError(code, message, xhr.status));
          return;
        }
        try {
          resolve(JSON.parse(xhr.responseText) as UploadResponse);
        } catch (e) {
          reject(
            new ApiError(
              'malformed_response',
              e instanceof Error ? e.message : 'Bad upload envelope',
              xhr.status,
            ),
          );
        }
      });
      const fd = new FormData();
      fd.append('file', file);
      xhr.send(fd);
    });
  },
};
